import {
  Box,
  Divider,
  Text,
  useBreakpointValue,
  useEventListener,
} from "@chakra-ui/react";
import TraitCard from "components/TraitCard";
import { swapableTraitCategories } from "config";
import useTranslate from "hooks/useTranslate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TraitCategory } from "types";
import { SwiperSlide } from "swiper/react/swiper-react";
import { Controller } from "swiper";
import Tab from "components/Tab";
import useCategoryIndex from "hooks/useCategoryIndex";
import { immutableReplaceAt } from "utils/arrayUtils";
import useDressingRoom from "hooks/useDressingRoom";
import EmptyState from "components/EmptyState";
import { getTraitsByCategory } from "utils/traitsUtils";
import ControlledSwiper from "components/ControlledSwiper";

type TraitsSelectorContainerProps = {
  renderCardBody?: (tokenId: number) => JSX.Element;
};

const TraitsSelectorContainer = ({
  renderCardBody,
}: TraitsSelectorContainerProps) => {
  const translate = useTranslate();

  const { initialTraits, currentTraits, availableTraits, color, swapTrait } =
    useDressingRoom();

  const [currentCategory, setCurrentCategory] = useState(
    swapableTraitCategories[0]
  );

  const traitsInCategory = useMemo(() => {
    const traits = getTraitsByCategory(
      currentCategory,
      availableTraits || [],
      initialTraits
    );
    return traits ? traits.reverse() : [];
  }, [currentCategory, availableTraits, initialTraits]);

  const currentCategoryIndex = useCategoryIndex(currentCategory);
  const selectedTraitIndex = useMemo(
    () =>
      traitsInCategory.findIndex(
        (tokenId) => currentTraits?.indexOf(tokenId) >= 0
      ),
    [traitsInCategory, currentTraits]
  );

  const slidesPerView = useBreakpointValue({
    base: 3.5,
    sm: 4.5,
  });
  const [tabsSwiper, setTabsSwiper] = useState<any>(null);
  const [carouselSwiper, setCarouselSwiper] = useState<any>(null);

  const handleCategoryChange = useCallback(
    (category: TraitCategory) => {
      setCurrentCategory(category);
    },
    [setCurrentCategory]
  );

  const handleSelect = useCallback(
    (tokenId: number) => {
      const isAlreadySelected = currentTraits.includes(tokenId);

      if (isAlreadySelected) {
        swapTrait(
          immutableReplaceAt<number>(
            currentTraits,
            currentCategoryIndex,
            initialTraits[currentCategoryIndex]
          )
        );
      } else {
        swapTrait(
          immutableReplaceAt<number>(
            currentTraits,
            currentCategoryIndex,
            tokenId
          )
        );
      }
    },
    [initialTraits, currentTraits, currentCategoryIndex, swapTrait]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      let index: number = -1;
      if (
        event.key === "ArrowRight" &&
        selectedTraitIndex < traitsInCategory.length - 1
      ) {
        index = selectedTraitIndex + 1;
      }
      if (event.key === "ArrowLeft" && selectedTraitIndex > 0) {
        index = selectedTraitIndex - 1;
      }

      if (index >= 0) {
        handleSelect(traitsInCategory[index]);
      }
    },
    [selectedTraitIndex, traitsInCategory, handleSelect]
  );

  useEffect(() => {
    if (carouselSwiper && selectedTraitIndex >= 0) {
      carouselSwiper.slideTo(selectedTraitIndex - 1);
    }
  }, [selectedTraitIndex, carouselSwiper]);

  useEffect(() => {
    if (tabsSwiper && currentCategoryIndex >= 0) {
      tabsSwiper.slideTo(currentCategoryIndex - 1);
    }
  }, [currentCategoryIndex, tabsSwiper]);

  useEventListener("keydown", handleKeyPress);

  return (
    <Box w="full" pb="3" px="4" bg="bright.500">
      <Box position="relative">
        <Divider position="absolute" bottom="0" left="0" right="0" />

        <ControlledSwiper
          spaceBetween={0}
          slidesPerView="auto"
          threshold={10}
          modules={[Controller]}
          onSwiper={setTabsSwiper}
          freeMode
        >
          {swapableTraitCategories.map((category) => (
            <SwiperSlide
              key={category}
              style={{
                width: "auto",
              }}
            >
              <Tab
                size="sm"
                isActive={currentCategory === category}
                onClick={() => handleCategoryChange(category)}
              >
                {translate(`traitCategories:${category}`)}
              </Tab>
            </SwiperSlide>
          ))}
        </ControlledSwiper>
      </Box>

      <Box mt="3">
        <ControlledSwiper
          spaceBetween={8}
          threshold={10}
          slidesPerView={slidesPerView}
          modules={[Controller]}
          onSwiper={setCarouselSwiper}
          freeMode
        >
          {traitsInCategory.map((tokenId) => (
            <SwiperSlide key={tokenId}>
              <Box p="2px">
                <TraitCard
                  key={tokenId}
                  tokenId={tokenId}
                  color={color}
                  isSelected={currentTraits[currentCategoryIndex] === tokenId}
                  imageSize="256"
                  onClick={() => {
                    handleSelect(tokenId);
                  }}
                >
                  {renderCardBody ? (
                    renderCardBody(tokenId)
                  ) : (
                    <Text
                      fontSize="sm"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      fontWeight="semibold"
                    >
                      #{tokenId}
                    </Text>
                  )}
                </TraitCard>
              </Box>
            </SwiperSlide>
          ))}
        </ControlledSwiper>

        {!traitsInCategory.length && <EmptyState py="7" opacity="0.8" />}
      </Box>
    </Box>
  );
};

export default TraitsSelectorContainer;
